import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronDown, Plus, Upload } from "lucide-react";
import { useWorkspaceList, useOrganizationMemberships } from "@wacht/tanstack-router";
import { useActiveOrganization } from "@wacht/tanstack-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CreateWorkspaceFormProps {
  organizationId: string;
  onSuccess?: (workspace?: any) => void;
  onCancel?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const CreateWorkspaceForm: React.FC<CreateWorkspaceFormProps> = ({
  organizationId: initialOrgId,
  onSuccess,
  onCancel,
  isOpen,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(initialOrgId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { createWorkspace } = useWorkspaceList();
  const { organizationMemberships } = useOrganizationMemberships();
  const { activeOrganization } = useActiveOrganization();

  // Use active organization if available, otherwise use initial
  const currentOrgId = activeOrganization?.id || selectedOrgId || initialOrgId;

  const selectedOrg = organizationMemberships?.find(
    (m: any) => m.organization.id === currentOrgId,
  )?.organization;

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size cannot exceed 2MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const getInitials = (text: string) => {
    return (
      text
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "W"
    );
  };

  const sanitizeText = (text: string): string => {
    return text.trim().replace(/[<>\"'&]/g, "");
  };

  const validateWorkspaceName = (name: string): boolean => {
    return (
      name.length >= 2 &&
      name.length <= 100 &&
      /^[a-zA-Z0-9\s_.-]+$/.test(name)
    );
  };

  const handleSubmit = async () => {
    const sanitizedName = sanitizeText(name);
    const sanitizedDescription = sanitizeText(description);

    if (!sanitizedName || !currentOrgId) {
      toast.error("Please enter a workspace name");
      return;
    }

    if (!validateWorkspaceName(sanitizedName)) {
      toast.error(
        "Workspace name must be 2-100 characters and contain only letters, numbers, spaces, dots, underscores, and hyphens",
      );
      return;
    }

    if (sanitizedDescription.length > 500) {
      toast.error("Description must be less than 500 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      const createdWorkspace = await createWorkspace(
        currentOrgId,
        sanitizedName,
        image || undefined,
        sanitizedDescription || undefined,
      );
      toast.success("Workspace created successfully");
      onSuccess?.(createdWorkspace);
      handleClose();
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to create workspace. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClose();
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setImage(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  }, [isOpen, previewUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create new workspace</DialogTitle>
          <DialogDescription>
            Workspaces are used to organize your projects and team members into
            logical groups.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {/* Left Panel - Avatar */}
          <div className="md:col-span-1 flex flex-col items-center justify-center p-8 bg-muted/50 rounded-lg border">
            <div
              className={cn(
                "w-20 h-20 rounded-2xl overflow-hidden border-2 border-border cursor-pointer transition-all relative group",
                previewUrl ? "border-transparent" : "bg-background",
              )}
              onClick={handleFileSelect}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Workspace logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-2xl font-semibold">
                  {getInitials(name || "W")}
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Upload className="w-4 h-4 text-white" />
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/png, image/jpeg, image/gif"
              disabled={isSubmitting}
              className="hidden"
            />
            <h3 className="mt-4 text-base font-normal">Create new workspace</h3>
            <p className="mt-2 text-sm text-muted-foreground text-center max-w-[200px]">
              Workspaces are used to organize your projects and team members
              into logical groups.
            </p>
          </div>

          {/* Right Panel - Form */}
          <div className="md:col-span-2 flex flex-col">
            <div className="mb-6">
              <h3 className="text-base font-normal mb-1">
                Choose your workspace name
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter a name for your new workspace. This will be visible to all
                members.
              </p>
            </div>

            <div className="space-y-4 flex-1">
              {/* Organization Selector - Read-only if only one org */}
              {organizationMemberships && organizationMemberships.length > 1 ? (
                <div className="space-y-2">
                  <Label>Organization</Label>
                  <Select
                    value={currentOrgId}
                    onValueChange={setSelectedOrgId}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                            {selectedOrg?.image_url ? (
                              <img
                                src={selectedOrg.image_url}
                                alt={selectedOrg.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              getInitials(selectedOrg?.name || "O").charAt(0)
                            )}
                          </div>
                          <span>{selectedOrg?.name || "Select organization"}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {organizationMemberships.map((membership: any) => (
                        <SelectItem
                          key={membership.organization.id}
                          value={membership.organization.id}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                              {membership.organization.image_url ? (
                                <img
                                  src={membership.organization.image_url}
                                  alt={membership.organization.name}
                                  className="w-full h-full object-cover rounded-full"
                                />
                              ) : (
                                getInitials(membership.organization.name).charAt(0)
                              )}
                            </div>
                            <span>{membership.organization.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Organization</Label>
                  <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/50">
                    <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                      {selectedOrg?.image_url ? (
                        <img
                          src={selectedOrg.image_url}
                          alt={selectedOrg.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        getInitials(selectedOrg?.name || "O").charAt(0)
                      )}
                    </div>
                    <span className="text-sm">{selectedOrg?.name || "Organization"}</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="workspace-name">Workspace name</Label>
                <Input
                  id="workspace-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Workspace Name"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="workspace-description">
                  Description (optional)
                </Label>
                <Textarea
                  id="workspace-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this workspace for?"
                  disabled={isSubmitting}
                  rows={4}
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              {onCancel ? (
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !name.trim()}
              >
                {isSubmitting ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

